import { applySessionPage, beginSessionPage, rejectSessionRecord, sessionPageRequest } from "./sessionTabState.js";

// The page reads behind a trace tab's record axis, over the host's list of
// open tabs: the page before the oldest turn shown, or the newest page again
// when the first read failed. getTrace opens the axis on the newest page; the
// transitions themselves live in sessionTabState.js. One read is in flight per
// tab at a time; a failed read leaves the axis as it was and says so, with the
// control offering a retry.
//
// `query(method, params)` is the data client; `tabs()` returns the open tabs
// as they are now; `setTabs(update)` takes a functional update.
export function createSessionTabReader({
  query,
  tabs,
  setTabs,
  warn = (...args) => console.warn(...args),
}) {
  const inflight = new Set();
  const patch = (id, update) => setTabs((prev) => prev.map((t) => (t.type === "trace" && t.id === id ? update(t) : t)));

  // Read the next page of the tab's record: the page before the oldest turn
  // shown, or the newest when nothing is on the axis. A request tab reads the
  // same record up to its own time (see sessionPageRequest). Resolves when
  // the read has settled; null when nothing was read.
  function readPage(id, seed = null) {
    if (inflight.has(id)) return null;
    const data = seed || tabs().find((t) => t.type === "trace" && t.id === id)?.data;
    const request = sessionPageRequest(data);
    if (!request) return null;
    const { sessionId, cursor, exclude, sinceMs, untilMs, focalEventId } = request;
    inflight.add(id);
    patch(id, beginSessionPage);
    return query("getSessionPage", { id: sessionId, cursor, exclude, sinceMs, untilMs, focalEventId })
      .then((page) => patch(id, (t) => applySessionPage(t, page, request)))
      .catch((error) => {
        // The axis keeps what it shows; the control becomes a retry.
        warn("[session] conversation page read failed:", error);
        patch(id, (t) => rejectSessionRecord(t, String(error?.message || error)));
      })
      .finally(() => inflight.delete(id));
  }

  return { readPage };
}
